/**
 * MemberCardForTier — renders the ACTUAL member card the storefront shows for a
 * customer's loyalty tier, reusing the rewards Card Designer's CardPreview.
 *
 * Resolution mirrors the storefront (RewardsHubCard) EXACTLY, so the dashboard
 * and storefront never diverge:
 *   1. uploaded full-bleed `tier_card_image`  →  <img>
 *   2. saved `tier_card_design` JSON          →  CardPreview
 *   3. neither (e.g. base tier with no design) → brand-text fallback
 *      (white card, store name in large black text — NOT an invented design)
 *
 * Data: useRewardsConfig(storefrontKey) returns the tiers for the CUSTOMER'S
 * store (loyalty is per-store); we match `loyalty_program_tier` (a name) to
 * `tier_name`. With no tier / no design, we render the same fallback the
 * storefront does — never a made-up card.
 */

import { cn } from "@/lib/utils";
import { useRewardsConfig } from "@/features/rewards/api/rewardsAdminHooks";
import { CardPreview } from "@/features/rewards/components/card-designer/CardPreview";
import { DEFAULT_DESIGN } from "@/features/rewards/components/card-designer/helpers";
import { useStoreList } from "@/hooks/useStoreList";

interface MemberCardForTierProps {
  /** Customer.loyalty_program_tier — a tier NAME string (may be empty). */
  tier?: string;
  /**
   * Customer's store (Customer.custom_primary_storefront). Loyalty programs are
   * per-store, so the card design must be fetched for the customer's OWN store.
   */
  storefrontKey?: string;
  /** Thumbnail scale (fills container width). Non-mini renders the 420px hero. */
  mini?: boolean;
}

export function MemberCardForTier({ tier, storefrontKey, mini }: MemberCardForTierProps) {
  const { data, isLoading } = useRewardsConfig(storefrontKey);
  const { data: stores } = useStoreList();

  const widthCls = mini ? "w-full" : "w-[420px]";
  const rounded = mini ? "rounded-lg" : "rounded-2xl";
  const shadow = mini ? "shadow-md" : "shadow-2xl";
  // Subtle edge so white cards don't float against the white panel.
  const ring = "ring-1 ring-black/10 dark:ring-white/15";

  if (isLoading) {
    return (
      <div
        className={cn("bg-muted animate-pulse", widthCls, rounded)}
        style={{ aspectRatio: "4 / 3" }}
      />
    );
  }

  const tierRow = tier ? data?.tiers?.find((t) => t.tier_name === tier) : undefined;
  const tierName = tier || "Member";
  const storeName = stores?.find((s) => s.storefront_key === storefrontKey)?.label;

  // 1. Uploaded full-bleed image wins.
  if (tierRow?.tier_card_image) {
    return (
      <div
        className={cn("relative overflow-hidden", widthCls, rounded, shadow, ring)}
        style={{ aspectRatio: "4 / 3" }}
      >
        <img
          src={tierRow.tier_card_image}
          alt={`${tierName} member card`}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>
    );
  }

  // 2. A real saved design → render it faithfully (wrapped so it gets the edge).
  if (tierRow?.tier_card_design) {
    const design = { ...DEFAULT_DESIGN, ...tierRow.tier_card_design };
    return (
      <div className={cn(widthCls, rounded, ring)}>
        <CardPreview design={design} tierName={tierName} mini={mini} />
      </div>
    );
  }

  // 3. No design/image for this tier → brand-text fallback (matches storefront).
  return (
    <div
      className={cn(
        "relative flex items-center justify-center overflow-hidden bg-white",
        widthCls,
        rounded,
        shadow,
        ring,
      )}
      style={{ aspectRatio: "4 / 3" }}
    >
      <span
        className={cn(
          "select-none px-4 text-center font-black tracking-tighter text-gray-900",
          mini ? "text-2xl" : "text-5xl",
        )}
      >
        {storeName || "Rewards"}
      </span>
    </div>
  );
}
